/**
 * Bending Spoons SDK (Orion experiments + PicoX analytics) wiring for
 * homepage-webapp. Mirrors the listings-webapp pattern documented in
 * `docs/orion-feature-flags.md`:
 *
 *   - The provider is mounted once at the highest stable spot in the React
 *     tree (`pages/_app.page.tsx`).
 *   - This module owns the typed `Settings` shape for the whole app.
 *     One key per Orion flag, default = current production behavior.
 *   - Feature code never imports `useOrionSettings` directly; it imports a
 *     typed wrapper hook from this file.
 *
 * homepage-webapp otherwise ramps via Statsig. The provider is also what makes `usePicoXTrack()` available to the PicoX
 * ad logger the interaction tracker runs (see `@/tracking`).
 *
 * To add a new flag: append one key + one default to `HomepageWebappSettings`
 * / `DEFAULT_SETTINGS`, then export a one-line wrapper hook below.
 */
import { useMemo } from 'react';
import { createAppDefaults } from '@eventbrite/bsp-sdk';
import { useOrionSettings } from '@eventbrite/bsp-sdk/react';
import type { GlobalHeaderProps } from '@eventbrite/consumer-header.global-header';
import { Stage } from '@eventbrite/webapp-constants';

export type HomepageWebappSettings = {
    enable_for_you_promoted_events: boolean;
    enable_creator_acquisition_prompt_on_homepage: boolean;
    header_create_events_direct_signup: boolean;
    header_use_2025_layout: boolean;
    header_creator_cta_orange: boolean;
    header_organizer_create_events_cta: boolean;
    header_organizer_get_started_orange: boolean;
};

export type HeaderCreateEventsSettings = Pick<
    HomepageWebappSettings,
    | 'header_create_events_direct_signup'
    | 'header_use_2025_layout'
    | 'header_creator_cta_orange'
    | 'header_organizer_create_events_cta'
    | 'header_organizer_get_started_orange'
>;

export type HeaderCreatorVariant = NonNullable<GlobalHeaderProps['creatorVariant']>;

const HEADER_CREATE_EVENTS_SETTING_KEYS: Array<keyof HeaderCreateEventsSettings> = [
    'header_create_events_direct_signup',
    'header_use_2025_layout',
    'header_creator_cta_orange',
    'header_organizer_create_events_cta',
    'header_organizer_get_started_orange',
];

const DEFAULT_SETTINGS = createAppDefaults<HomepageWebappSettings>({
    // Default `true`: the For-you auction already shipped (eb-web#164) before this gate existed, so `true` is
    // current production behavior. This flag exists as a kill switch — flip the Orion segment to `false` if the
    // auction turns out to cannibalize organic ticket orders, without a code deploy.
    enable_for_you_promoted_events: true,
    enable_creator_acquisition_prompt_on_homepage: false,
    header_create_events_direct_signup: false,
    header_use_2025_layout: false,
    header_creator_cta_orange: false,
    header_organizer_create_events_cta: false,
    header_organizer_get_started_orange: false,
});

// Both inputs here are deliberately chosen to resolve identically on the server render and
// the client hydration render. `Stage` is resolved server-side from `process.env.STAGE` and
// handed down as a prop (see `App.getInitialProps` in `_app.page.tsx`), and `NODE_ENV` is
// inlined at build time. Deriving this from `window.location.hostname` cannot be: `window`
// is undefined during SSR, so that render resolves every host to `production` and disagrees
// with the client — pointing Orion at production settings while serving a QA host.
//
// NODE_ENV is checked first because local dev runs with `STAGE=Test` (see `.env.development`)
// so that its service clients talk to QA; without this branch a local `next dev` would report
// `staging` rather than `development`.
const bspEnvironmentFromStage = (stage: Stage): 'staging' | 'development' | 'production' => {
    if (process.env.NODE_ENV === 'development') {
        return 'development';
    }
    if (stage === Stage.TEST) {
        return 'staging';
    }
    if (stage === Stage.DEV || stage === Stage.LOCAL) {
        return 'development';
    }
    return 'production';
};

export const useBspConfig = (stage: Stage) =>
    useMemo(
        () =>
            ({
                appName: 'homepage-webapp',
                environment: bspEnvironmentFromStage(stage),
                defaultOrionSettings: DEFAULT_SETTINGS,
                defaultSegmentations: {},
            }) as const,
        [stage],
    );

/**
 * Gate for the For-you promoted-events auction (`enable_for_you_promoted_events` Orion flag).
 *
 * This is a kill switch, not a ramp: the auction is already live for every authenticated For-you visitor, and this
 * flag exists solely so it can be turned off by segment without a deploy if it turns out to cannibalize organic
 * ticket orders. Defaults to `true` (current behavior) so a network failure or an unconfigured flag is a no-op.
 *
 * Returns `isLoading`, but callers don't need to hold on it: the fallback
 * value while loading is the same `true` as the shipped default, so there's no flash of a different auction state.
 */
export const useIsForYouPromotedEventsEnabled = (): {
    isEnabled: boolean;
    isLoading: boolean;
} => {
    let settings: Partial<HomepageWebappSettings> = {};
    let isLoading = false;
    try {
        const state = useOrionSettings<HomepageWebappSettings>();
        settings = state.settings;
        isLoading = state.isLoading;
    } catch {
        // Swallow — useOrionSettings throws on the SSR render path (no BspProvider).
    }
    const isEnabled =
        settings.enable_for_you_promoted_events ?? DEFAULT_SETTINGS.enable_for_you_promoted_events;
    return { isEnabled, isLoading };
};

export const useIsCreatorAcquisitionPromptOnHomepageEnabled = (): {
    isEnabled: boolean;
    isLoading: boolean;
} => {
    let settings: Partial<HomepageWebappSettings> = {};
    let isLoading = false;
    try {
        const state = useOrionSettings<HomepageWebappSettings>();
        settings = state.settings;
        isLoading = state.isLoading;
    } catch {
        settings = {};
        isLoading = false;
    }
    const isEnabled =
        !isLoading &&
        (settings.enable_creator_acquisition_prompt_on_homepage ??
            DEFAULT_SETTINGS.enable_creator_acquisition_prompt_on_homepage);
    return { isEnabled, isLoading };
};

/**
 * Header experiment settings are owned by Janus. Keep a treatment hidden until
 * Orion settles so missing, malformed, and SSR-only values preserve control.
 */
export const resolveHeaderCreatorVariant = (
    rawSettings: unknown,
    isLoading: boolean,
    isServer = typeof window === 'undefined',
): HeaderCreatorVariant => {
    if (isLoading || isServer || !rawSettings || typeof rawSettings !== 'object') {
        return 'control';
    }

    const settings = rawSettings as Record<string, unknown>;
    if (
        HEADER_CREATE_EVENTS_SETTING_KEYS.some(
            (setting) => settings[setting] !== undefined && typeof settings[setting] !== 'boolean',
        )
    ) {
        return 'control';
    }

    const directSignup = settings.header_create_events_direct_signup === true;
    const use2025Layout = settings.header_use_2025_layout === true;
    const orangeCreatorCta = settings.header_creator_cta_orange === true;
    const organizerCreateEvents = settings.header_organizer_create_events_cta === true;
    const orangeGetStarted = settings.header_organizer_get_started_orange === true;

    if (
        directSignup &&
        !use2025Layout &&
        !orangeCreatorCta &&
        !organizerCreateEvents &&
        !orangeGetStarted
    ) {
        return 'direct-signup';
    }

    if (
        !directSignup &&
        use2025Layout &&
        !orangeCreatorCta &&
        organizerCreateEvents &&
        !orangeGetStarted
    ) {
        return 'restore-2025-layout';
    }

    if (
        directSignup &&
        !use2025Layout &&
        orangeCreatorCta &&
        organizerCreateEvents &&
        orangeGetStarted
    ) {
        return 'orange-direct-signup';
    }

    return 'control';
};

export const useHeaderCreateEventsSettings = (): {
    settings: HeaderCreateEventsSettings;
    isLoading: boolean;
} => {
    let orionSettings: unknown = {};
    let isLoading = false;
    try {
        const state = useOrionSettings<HomepageWebappSettings>();
        orionSettings = state.settings;
        isLoading = state.isLoading;
    } catch {
        // SSR without a provider must retain the shared HTML control variant.
    }

    const settings = orionSettings as Record<string, unknown>;
    const hasMalformedSettings =
        !orionSettings ||
        typeof orionSettings !== 'object' ||
        HEADER_CREATE_EVENTS_SETTING_KEYS.some(
            (setting) => settings[setting] !== undefined && typeof settings[setting] !== 'boolean',
        );

    return {
        settings: {
            header_create_events_direct_signup:
                !isLoading &&
                !hasMalformedSettings &&
                settings.header_create_events_direct_signup === true,
            header_use_2025_layout:
                !isLoading && !hasMalformedSettings && settings.header_use_2025_layout === true,
            header_creator_cta_orange:
                !isLoading && !hasMalformedSettings && settings.header_creator_cta_orange === true,
            header_organizer_create_events_cta:
                !isLoading &&
                !hasMalformedSettings &&
                settings.header_organizer_create_events_cta === true,
            header_organizer_get_started_orange:
                !isLoading &&
                !hasMalformedSettings &&
                settings.header_organizer_get_started_orange === true,
        },
        isLoading,
    };
};
